import { ApiClient } from '../config';

export interface SystemHealth {
  status: string;
  database: {
    connected: boolean;
    tables: unknown[];
  };
  activeCustomers: number;
  activeLicenses: number;
  totalActivations: number;
  errorRate: number;
  avgResponseTime: number | null;
  timestamp: string;
}

export interface TrendPoint {
  period: string;
  count: number;
}

export interface UsageTrends {
  activations: TrendPoint[];
  customers: TrendPoint[];
  licenses: TrendPoint[];
}

export interface CustomerMetric {
  customerId: string;
  company: string;
  totalLicenses: number;
  activeLicenses: number;
  totalActivations: number;
  activeUsers: number;
  usageStats: {
    totalValidations: number;
    failedValidations: number;
    avgResponseTime: number | null;
  };
}

export interface CustomerMetrics {
  summary: { total: number; active: number };
  byType: CustomerMetric[];
}

export interface AnalyticsDateRange {
  startDate: string;
  endDate: string;
}

export interface AnalyticsService {
  getSystemHealth(): Promise<SystemHealth>;
  getUsageTrends(range: AnalyticsDateRange): Promise<UsageTrends>;
  getCustomerMetrics(range: AnalyticsDateRange): Promise<CustomerMetrics>;
}

export class AnalyticsServiceImpl implements AnalyticsService {
  private apiClient: ApiClient;

  constructor(apiClient: ApiClient) {
    this.apiClient = apiClient;
  }

  // NOTE: /api/v1/analytics/health, /trends and /customers all respond with a
  // { data, success, error } envelope. ApiClient.request() auto-unwraps the
  // `data` property (see handleResponse in ../config), which also drops the
  // sibling `success`/`error` fields. The backend currently only sets
  // success:false when the underlying DB tables don't exist (still HTTP 200),
  // in which case these methods resolve with the service's built-in empty
  // fallback data instead of throwing - a narrow, documented behavior change
  // from the previous raw-fetch implementation, which threw in that case.
  // Real HTTP errors (network failures, 401, 5xx) still reject exactly as before.

  async getSystemHealth(): Promise<SystemHealth> {
    return this.apiClient.get<SystemHealth>('/api/v1/analytics/health');
  }

  async getUsageTrends(range: AnalyticsDateRange): Promise<UsageTrends> {
    return this.apiClient.get<UsageTrends>('/api/v1/analytics/trends', {
      startDate: range.startDate,
      endDate: range.endDate,
    });
  }

  async getCustomerMetrics(range: AnalyticsDateRange): Promise<CustomerMetrics> {
    return this.apiClient.get<CustomerMetrics>('/api/v1/analytics/customers', {
      startDate: range.startDate,
      endDate: range.endDate,
    });
  }
}

export const analyticsService = new AnalyticsServiceImpl(new ApiClient());

export function createAnalyticsService(apiClient: ApiClient): AnalyticsService {
  return new AnalyticsServiceImpl(apiClient);
}
